
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Shield, Lock, Users, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { submitBetaRequest } from '@/services/betaRequestService'

export default function RequestAccess() {
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    useCase: ''
  })
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      // Submit to API
      const response = await submitBetaRequest({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        use_case: formData.useCase
      })
      
      // Also store in localStorage as backup
      const requests = JSON.parse(localStorage.getItem('betaRequests') || '[]')
      requests.push({
        ...formData,
        timestamp: new Date().toISOString(),
        request_id: response.request_id
      })
      localStorage.setItem('betaRequests', JSON.stringify(requests))
      
      setSubmitted(true)
      
      toast({
        title: "Request Received!",
        description: response.emails_sent?.confirmation_sent 
          ? "Check your email for confirmation. We'll review your application within 24-48 hours."
          : "We'll review your application and get back to you within 24-48 hours.",
      })
    } catch (error) {
      console.error('Beta request error:', error)
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to submit request. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full mb-4 mx-auto">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Request Received!</CardTitle>
            <CardDescription className="text-base">
              Thank you for your interest in Form Entry Fraud Detection.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm">
                <strong>What happens next:</strong>
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                <li>• We'll review your application within 24-48 hours</li>
                <li>• You'll receive an email with access instructions</li>
                <li>• Private beta includes full features for 30 days free</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <Button asChild className="w-full">
                <Link to="/">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Return to Homepage
                </Link>
              </Button>
              
              <p className="text-center text-sm text-muted-foreground">
                Want to explore now?{' '}
                <Link to="/" className="text-primary hover:underline font-medium">
                  Try our live demo
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4 mx-auto">
            <Shield className="h-8 w-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Request Private Beta Access</CardTitle>
          <CardDescription className="text-base">
            Join our exclusive private beta program
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Private Beta Notice */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
            <div className="flex gap-3">
              <Lock className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-medium text-amber-900 dark:text-amber-100">
                  Private Beta Program
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  We're currently in private beta and carefully selecting partners who can benefit most from our fraud detection platform.
                </p>
              </div>
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-muted/50 rounded-lg p-4 mb-6 space-y-2">
            <p className="font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Private Beta Benefits:
            </p>
            <ul className="text-sm text-muted-foreground space-y-1 ml-6">
              <li>• 30 days free access to all features</li>
              <li>• Direct support from our engineering team</li>
              <li>• Influence product roadmap with your feedback</li>
              <li>• Special pricing when we launch publicly</li>
            </ul>
          </div>

          {/* Request Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Your Name *</Label>
              <Input
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Smith"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Work Email *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john@company.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company *</Label>
              <Input
                id="company"
                required
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                placeholder="Acme Corp"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="useCase">
                How would you use fraud detection? *
              </Label>
              <Textarea
                id="useCase"
                required
                value={formData.useCase}
                onChange={(e) => setFormData({ ...formData, useCase: e.target.value })}
                placeholder="Describe your use case, current challenges with fraud, and expected lead volume..."
                rows={4}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </form>

          {/* Footer Links */}
          <div className="mt-6 space-y-3">
            <p className="text-center text-sm text-muted-foreground">
              Want to explore the platform first?
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/">
                Try Live Demo (No signup required)
              </Link>
            </Button>
            
            <p className="text-center text-sm text-muted-foreground">
              Already have access?{' '}
              <Link to="/auth/login" className="text-primary hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
